import {
  Box,
  Checkbox,
  Chip,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { ImageWithVideoFallback } from 'components/common/Image';
import { Tooltip } from 'components/common/Tooltip';
import { typography } from 'components/common/Typography/styles';
import { CreatorAvatar } from 'features/creator';
import { platformIconMap } from 'features/socialPost';
import { CompetitorShareOfVoiceAggregatedDataItemFragmentBcShareOfVoiceTableFragment } from 'graphql/generated';
import { Link, useNavigate } from 'react-router-dom';
import { PlotRoutes } from 'Routes';
import { theme } from 'styles/theme';
import { formatBigNumber } from 'utils/number';

export type BrandCompetitorsShareOfVoiceTableRowProps = {
  brandId: string;
  item: CompetitorShareOfVoiceAggregatedDataItemFragmentBcShareOfVoiceTableFragment;
  isSelected: boolean;
  color?: string;
  onSelect: () => void;
};

export const BrandCompetitorsShareOfVoiceTableRow = (
  props: BrandCompetitorsShareOfVoiceTableRowProps,
) => {
  const { brandId, item, isSelected, color, onSelect } = props;

  const navigate = useNavigate();

  const creatorWithProfilePicture = item.base.brand.creators.find(
    (creator) => creator.profilePictureUrl,
  );
  const firstCreator = item.base.brand.creators[0];
  const FirstCreatorPlatformIcon = platformIconMap[firstCreator?.platform];

  return (
    <TableRow
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();

        if (!item.base.competitor) {
          return;
        }

        navigate(
          PlotRoutes.socialListeningCompetitor(
            item.base.competitor.id,
            brandId,
          ),
        );
      }}
      sx={{
        cursor: 'pointer',
      }}
    >
      <TableCell>
        <Checkbox
          checked={isSelected}
          onClick={(e) => {
            e.stopPropagation();
          }}
          onChange={onSelect}
          size="small"
          sx={{
            p: 0,
          }}
        />
      </TableCell>
      <TableCell>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 12,
              height: 12,
              bgcolor: color,
              borderRadius: 6,
            }}
          />
          {creatorWithProfilePicture && (
            <CreatorAvatar
              creator={creatorWithProfilePicture}
              size={24}
              hidePlatformIcon
            />
          )}
          <Typography variant="headline-sm">{item.base.brand.name}</Typography>
          {firstCreator ? (
            <Tooltip
              title={
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                  }}
                >
                  {item.base.brand.creators.map((creator) => {
                    const Icon = platformIconMap[creator.platform];

                    return (
                      <Box
                        key={creator.platform}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <Icon size={12} />
                        <Typography variant="body-sm">
                          {creator.handle}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              }
            >
              <Chip
                size="small"
                variant="filled-borderless"
                icon={
                  <FirstCreatorPlatformIcon
                    size={12}
                    style={{
                      color: theme.colors?.utility[600],
                    }}
                  />
                }
                label={`${firstCreator.handle}${
                  item.base.brand.creators.length > 1
                    ? ` +${item.base.brand.creators.length - 1}`
                    : ''
                }`}
                sx={{
                  ml: 1,
                  span: {
                    ...typography['headline-xxs'],
                  },
                }}
              />
            </Tooltip>
          ) : null}
        </Box>
      </TableCell>
      <TableCell>
        <Typography variant="subhead-lg">
          {isSelected
            ? `${Number(item.aggregatedData.shareOfVoice * 100).toFixed(2)}%`
            : 'n/a'}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="subhead-lg">
          {formatBigNumber(item.aggregatedData.mentionCount)}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="subhead-lg">
          {formatBigNumber(item.aggregatedData.viewCount)}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="subhead-lg">
          {formatBigNumber(item.aggregatedData.totalEngagement)}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="subhead-lg">
          {Number(item.aggregatedData.engagementRate * 100).toFixed(2)}%
        </Typography>
      </TableCell>
      <TableCell>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {item.base.previewPosts.map((post) => (
            <Link
              to={PlotRoutes.socialListeningPost(post.id, brandId)}
              target="_blank"
            >
              <ImageWithVideoFallback
                imageUrl={post.thumbnailUrl ?? ''}
                videoUrl={post.videoUrl ?? ''}
                sx={{
                  width: 24,
                  height: 32,
                  borderRadius: 1,
                  objectFit: 'cover',
                }}
              />
            </Link>
          ))}
        </Box>
      </TableCell>
    </TableRow>
  );
};
